import type { UpdateAppointmentTypeInput } from 'app/generated/gql/graphql'
import { z } from 'zod'
import { useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { EditAppointmentTypeModal } from 'app/components/scheduling/EditAppointmentTypeModal'
import { AIAgentConfiguration, AIAgentConfigurationType } from 'app/types/entities'
import { AppointmentTypeType } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'

import { useAppointmentTypeStore } from 'app/stores/StoreContext'
import { logError } from 'lib/logging'

// Editable UpdateAppointmentTypeInput fields keyed by the generated input shape.
// `.strict()` rejects any field name the UI does not support; one field is sent
// per call, so every key is optional. z.input matches the mutation input type.
const appointmentTypeFieldSchema = z
  .object({
    patientType: z.string(),
    activeScheduler: z.boolean(),
    activeAgent: z.boolean(),
    blockScheduling: z.boolean(),
    doubleBookingExclusion: z.boolean(),
    generalBuffer: z.number().int(),
    morningBuffer: z.number().int(),
    blockSchedulingNotes: z.array(z.string()),
    excludedBlockouts: z.array(z.string()),
  })
  .strict()
  .partial()

function sortByOrder(items: AppointmentTypeType[]): AppointmentTypeType[] {
  return [...items].sort((a, b) => {
    const aOrder = a.order ?? Number.MAX_SAFE_INTEGER
    const bOrder = b.order ?? Number.MAX_SAFE_INTEGER
    return aOrder - bOrder
  })
}

function sortByStatus(items: AppointmentTypeType[]): AppointmentTypeType[] {
  const enabled = items.filter((item) => item.activeScheduler)
  const disabled = items.filter((item) => !item.activeScheduler)
  return [...sortByOrder(enabled), ...sortByOrder(disabled)]
}

export type UseAvailabilityAppointmentTypesReturn = {
  rows: AppointmentTypeType[]
  agentConfigState: any
  isSyncing: boolean
  isCarestack: boolean
  allowDoubleBooking: boolean
  excludableAppointmentTypes: { id: string; name: string }[]
  onFieldChange: (id: string, field: string, value: unknown) => Promise<void>
  onAppointmentTypeUpdated: (appointmentType: AppointmentTypeType) => void
  openEditAppointmentTypeModal: (appointmentType: AppointmentTypeType) => void
  onRefreshClick: () => Promise<void>
  handleAppointmentCategoryChange: (appointmentTypeId: string, categories: string[]) => void
}

export function useAvailabilityAppointmentTypes(): UseAvailabilityAppointmentTypesReturn {
  const { locationId } = useParams()
  const appointmentTypeStore = useAppointmentTypeStore()
  const [rows, setRows] = useState<AppointmentTypeType[]>([])
  const [agentConfigState, setAgentConfigState] = useState<any>()
  const [excludableAppointmentTypes, setExcludableAppointmentTypes] = useState<{ id: string; name: string }[]>([])
  const [allowDoubleBooking, setAllowDoubleBooking] = useState(false)
  const [isCarestack, setIsCarestack] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  useEffect(() => {
    if (!locationId) return
    fetch(`/api/scheduling/locations/${locationId}/appointment-types`, { credentials: `include` })
      .then((res) => res.json())
      .then((data) => {
        setRows(sortByStatus((data.appointmentTypes ?? []) as AppointmentTypeType[]))
        setAgentConfigState(data.agentConfig)
        setExcludableAppointmentTypes(data.excludableAppointmentTypes ?? [])
        setAllowDoubleBooking(data.allowDoubleBooking ?? false)
        setIsCarestack(data.isCarestack ?? false)
      })
      .catch((err) => logError(`Error fetching appointment types`, err))
  }, [locationId])

  const onFieldChange = async (id: string, field: string, value: unknown) => {
    const parsed = appointmentTypeFieldSchema.safeParse({ [field]: value })
    if (!parsed.success) {
      notifyError(`Unsupported appointment type setting`)
      return
    }
    const input: UpdateAppointmentTypeInput = parsed.data
    const result = await appointmentTypeStore.update(id, input)
    if (!result.error) {
      notifySuccess(`Appointment type updated`)
      setRows((prev) => sortByStatus(prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))))
    } else {
      notifyError(`Error updating appointment type`)
    }
  }

  const onAppointmentTypeUpdated = (appointmentType: AppointmentTypeType) => {
    setRows((prev) => prev.map((row) => (row.id === appointmentType.id ? appointmentType : row)))
  }

  const handleSaveAppointmentType = async (id: string, input: UpdateAppointmentTypeInput) => {
    const result = await appointmentTypeStore.update(id, input)
    if (result.error) return undefined
    const data = result.data as { updateAppointmentType?: AppointmentTypeType } | undefined
    return data?.updateAppointmentType
  }

  const openEditAppointmentTypeModal = (appointmentType: AppointmentTypeType) => {
    showModal(
      <EditAppointmentTypeModal
        onAppointmentTypeUpdated={onAppointmentTypeUpdated}
        appointmentType={appointmentType}
        excludableAppointmentTypes={excludableAppointmentTypes}
        allowDoubleBooking={allowDoubleBooking}
        isUnified
        agentConfig={agentConfigState as AIAgentConfigurationType}
        onAppointmentCategoryChange={handleAppointmentCategoryChange}
        onSave={handleSaveAppointmentType}
      />,
      {
        title: `Edit Appointment type`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  const onRefreshClick = async () => {
    setIsSyncing(true)
    fetch(`/api/scheduling/locations/${locationId}/appointment-types`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({ action: `sync` }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        setIsSyncing(false)
        if (data?.action === `sync`) {
          if (data.success) {
            const added = data.added as number
            const deleted = data.deleted as number
            notifySuccess(
              added
                ? `${added} New Appointment Types Added`
                : `No New Appointment Types Added` + (deleted ? `,\n ${deleted} Appointment Types Deleted` : ``),
            )
            if (data.appointmentTypes) {
              setRows(sortByStatus(data.appointmentTypes as AppointmentTypeType[]))
            }
          } else {
            notifyError(`Error syncing appointment types`)
          }
        }
      })
      .catch((err) => {
        setIsSyncing(false)
        notifyError(`Error syncing appointment types`)
        logError(`Error syncing appointment types`, err)
      })
  }

  const handleAppointmentCategoryChange = (appointmentTypeId: string, categories: string[]) => {
    fetch(`/api/scheduling/locations/${locationId}/appointment-types`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({ action: `map-appointment-type`, appointmentTypeId, categories }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        if (data?.success) {
          // The map response is active-only (TRK-5787). Rebuilding the list from it dropped
          // disabled types until a refresh (TRK-5835), so apply only agentConfig and keep
          // the full list already loaded from the admin GET.
          if (data.agentConfig) setAgentConfigState(data.agentConfig as AIAgentConfigurationType)
        } else {
          notifyError(`Error mapping appointment type`)
        }
      })
      .catch((err) => {
        notifyError(`Error mapping appointment type`)
        logError(`Error mapping appointment type`, err)
      })
  }

  return {
    rows,
    agentConfigState,
    isSyncing,
    isCarestack,
    allowDoubleBooking,
    excludableAppointmentTypes,
    onFieldChange,
    onAppointmentTypeUpdated,
    openEditAppointmentTypeModal,
    onRefreshClick,
    handleAppointmentCategoryChange,
  }
}
